/**
 * Die Szene: Wasser, Himmel, ein Fahrzeug, seine Lichter, eine Kamera, die darum kreist.
 * Bewusst reduziert, denn die Lichter sind der Inhalt.
 *
 * Die Sektor- und Tragweitenlogik liegt in `src/core/geometry.ts` und wird hier nur
 * angewendet, nicht wiederholt.
 */
import {
  AdditiveBlending,
  AmbientLight,
  BackSide,
  Color,
  DirectionalLight,
  FogExp2,
  Group,
  Mesh,
  PerspectiveCamera,
  Scene,
  ShaderMaterial,
  SphereGeometry,
  Sprite,
  SpriteMaterial,
  Texture,
  Vector3,
  WebGLRenderer,
} from 'three'
import { NM_IN_METERS } from '@/core/geometry'
import { binnenState, buoy, istBinnen, lightState, scenario as scenarioById, vessel } from '@/core/data'
import { bearingFromOther, positionOf } from '@/core/scenario'
import { applyDaylight, buildVessel, disposeVessel, type HullKind, type VesselModel } from './hulls'
import { NIGHT_VISION_FILTER_ID, applyNightVision } from './nightVision'
import { createLights, disposeLights, updateLights, type RenderLight } from './lights'
import type { SignalStep } from '@/core/signals'
import { createShapes, disposeShapes, setShapesVisible, type RenderShape } from './shapes'
import { loadedHull, preloadHulls, vesselFromHull } from './modelLoader'
import { preloadTexturen, textur } from './texturen'
import { buildBuoy, disposeBuoy, updateBuoyLight, type BuoyModel } from './buoys'
import { createWater, MOON_DIRECTION, WATER_DAY, WATER_NIGHT, type WaterLight } from './water'

export interface SceneOptions {
  canvas: HTMLCanvasElement
  /** Im Testbetrieb: fester Zeitgeber, Pixelverhältnis 1, lesbarer Framebuffer. */
  testMode?: boolean
}

export interface LightSample {
  type: string
  colour: string
  mount: string
  intensity: number
  /** Bildschirmposition in CSS-Pixeln, Ursprung links oben. */
  x: number
  y: number
  onScreen: boolean
}

export interface ShapeSample {
  kind: string
  mount: string
  visible: boolean
  x: number
  y: number
}

export interface SceneHandle {
  setStateId(id: string): void
  /** Zeigt ein Seezeichen statt eines Fahrzeugs. */
  setBuoyId(id: string): void
  /**
   * Zeigt eine Ausweichsituation aus der Sicht des eigenen Fahrzeugs: die Kamera steht
   * auf der eigenen Brücke, das andere Fahrzeug liegt nach Peilung und Entfernung.
   */
  setScenarioId(id: string): void
  /** Blickrichtung in der Ausweichsituation, relativ zum eigenen Kurs. */
  setLookDeg(deg: number): void
  /** Helligkeit des Feuers im letzten Bild, 0 bis 1. */
  buoyLight(): number
  /** Feuer des Seezeichens mit seiner Stelle im Bild, für die Messung am Pixel. */
  sampleBuoyLight(): LightSample | undefined
  setBearing(deg: number): void
  setElevation(norm: number): void
  setDistanceNm(nm: number): void
  setDayMode(day: boolean): void
  /** Rotmodus: die ganze Fläche wird rot, damit die Nachtsicht bleibt. */
  setNightVision(on: boolean): void
  /** Mondphase, 0 Neumond bis 1 Vollmond. */
  setMondphase(phase: number): void
  setTime(sec: number): void
  /** Dünung und Wellen an oder aus. */
  setMotion(on: boolean): void
  /** Lädt die gelieferten Rümpfe; danach wechselt der Zustand ohne Warten. */
  preload(): Promise<void>
  /** Entfernung, bei der das Fahrzeug formatfüllend steht. */
  fitDistanceNm(): number
  resize(): void
  start(): void
  stop(): void
  renderOnce(): void
  sampleLights(): LightSample[]
  /**
   * Das Lichtzeichen der Binnenschifffahrt nach § 4.01 Nummer 2: ein gelbes, helles, von
   * allen Seiten sichtbares Licht, das jedes Schallzeichen eines Maschinenfahrzeugs in
   * gleicher Dauer begleitet. Es kommt nicht aus den Daten des Lichterbildes, sondern aus
   * dem Ton, der gerade läuft: gesetzt wird die Zeitleiste des Schallzeichens, und das
   * Licht brennt während der Töne und ist in den Pausen aus. Ohne Zeitleiste erlischt es.
   */
  setLichtzeichen(schritte?: SignalStep[]): void
  /** Für die Prüfung: wo das Lichtzeichen im Bild steht und ob es gerade brennt. */
  sampleLichtzeichen(): { x: number; y: number; an: boolean } | undefined
  sampleShapes(): ShapeSample[]
  readPixel(x: number, y: number): [number, number, number, number]
  currentStateId(): string
  /**
   * Für die Prüfung: die Stärken der Lichtquellen, wie sie gerade gesetzt sind.
   *
   * Die Nacht folgt dem Mond über vier Wege: Himmel, Umgebungslicht, Restlicht aus der
   * Gegenrichtung und Streiflicht. Am Bild ist davon nur die Summe zu sehen, und ein
   * Verifier hat gezeigt, wie wenig das taugt: drei der vier Wege ließen sich einzeln
   * abklemmen, ohne dass eine Pixelprüfung rot wurde, weil der Dunst über der Messstelle
   * den Ausfall auffing. Was eine Prüfung behauptet, muss sie auch treffen, also liest
   * sie die Stärken hier direkt.
   */
  sampleBeleuchtung(): {
    ambient: number
    sun: number
    rim: number
    sterne: number
    himmel: { r: number; g: number; b: number }
  }
  /** Für die Prüfung: wo die Mondscheibe im Bild steht und wie groß sie dort ist. */
  sampleMoon(): { x: number; y: number; radius: number; visible: boolean }
  /**
   * Für die Prüfung: den Ausschnitt um den Mond auf das ganze Bild rendern.
   *
   * Der Mond ist im Bild zehn Bildpunkte breit, und das ist richtig so, denn am Himmel ist
   * er ein halbes Grad breit. Eine Prüfung, die die Form seiner Schattenkante misst,
   * braucht trotzdem Auflösung. Sie bekommt sie wie ein Beobachter mit Fernglas: derselbe
   * Mond, näher herangeholt, nicht ein für die Messung aufgeblasener. `setViewOffset` von
   * three rendert dafür einen Ausschnitt des Bildes in voller Auflösung, die Szene bleibt
   * Bild für Bild dieselbe. Ohne Faktor oder mit 1 ist der Zustand wieder der normale.
   */
  zoomeAufMond(faktor: number): void
  dispose(): void
}

const SKY_VERTEX = `
varying vec3 vWorld;
varying vec2 vUv;
void main() {
  vWorld = position;
  // Die Kugel bringt ihre äquirektangulären Koordinaten mit. Sie hier zu nehmen spart
  // dem Fragmentshader ein atan und ein acos je Bildpunkt, und die Himmelskugel füllt
  // den ganzen Bildschirm.
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`

/**
 * Der Himmel: Verlauf und darüber das gelieferte Sternenfeld.
 *
 * Vorher waren die Sterne 700 Punkte mit fester Größe, alle gleich hell. Das gelieferte
 * Bild (`assets/texturen/sternenhimmel.png`, 2048 × 1024, äquirektangulär) bringt die
 * Helligkeitsstufen und das Band der Milchstraße mit. Es liegt auf der Innenseite der
 * Kugel und wird über deren eigene Koordinaten gelesen.
 *
 * Tagsüber verschwindet es vollständig, und nachts bleibt es gedämpft: die Lichter des
 * Fahrzeugs müssen das Hellste im Bild bleiben, darauf beruhen die Pixelprüfungen.
 */
const SKY_FRAGMENT = `
uniform vec3 top;
uniform vec3 horizon;
uniform sampler2D sterne;
uniform float sternenStaerke;
varying vec3 vWorld;
varying vec2 vUv;
void main() {
  vec3 richtung = normalize(vWorld);
  float h = clamp(richtung.y * 2.2 + 0.18, 0.0, 1.0);
  vec3 farbe = mix(horizon, top, h);
  if (sternenStaerke > 0.001) {
    // Am Horizont blendet das Feld aus: dort steht der Dunst, und dort liegen die Lichter.
    float hoehe = smoothstep(0.0, 0.22, richtung.y);
    if (hoehe > 0.0) farbe += texture2D(sterne, vUv).rgb * sternenStaerke * hoehe;
  }
  gl_FragColor = vec4(farbe, 1.0);
  /**
   * Der Himmel rechnet linear, das Bild ist sRGB.
   *
   * three nimmt die Farben der Palette als sRGB entgegen und legt sie linear in den
   * Uniforms ab. Ein eigener Shader schreibt sie von dort ohne Umweg ins Bild, und
   * deshalb fehlte ihm genau der Schritt zurück, den three jedem Standardmaterial über
   * diesen Baustein mitgibt. Der Himmel stand damit um ein Vielfaches zu dunkel im Bild:
   * gemessen RGB 1/1/3, wo die Palette ein sichtbares Dunkelblau vorgibt. Das war der
   * Grund, warum der Rumpf gegen ihn in jeder Mondphase hell wirkte, und keine
   * Nachtpalette der Welt hätte das geradegebogen.
   */
  #include <colorspace_fragment>
}
`

/**
 * Die Mondscheibe, in Metern auf 20 km Entfernung.
 *
 * Der Mond misst am Himmel 0,52 Grad, ein halbes Grad, nicht mehr als ein Daumennagel auf
 * Armeslänge. Hier stand er auf 900, also auf 2,5 Grad: fast fünfmal so breit und mit der
 * dreiundzwanzigfachen Fläche. Das ist der Grund, warum die Nacht zu hell wirkte, denn
 * nicht die Scheibe pro Fläche war zu hell, sondern es war zu viel Scheibe.
 *
 * 360 ergibt 1,03 Grad, also das Doppelte der Wirklichkeit. Die Überzeichnung bleibt mit
 * Absicht: der Mond steht hier 6,5 Grad über dem Horizont, und dort nimmt ihn jeder Mensch
 * größer wahr, als er ist (die Mondtäuschung, seit Ptolemäus beschrieben und bis heute
 * nicht restlos erklärt). Faktor zwei ist die obere Grenze dessen, was diese Täuschung
 * trägt. Wie hell die Nacht wird, hängt ohnehin nicht an der Scheibe, sondern am
 * Phasenwert, der Rumpf und Wasserschein steuert.
 */
const MOND_GROESSE = 360

/** Wie stark das Sternenfeld über dem Verlauf liegt. Über 0.6 überstrahlt es die Lichter. */
const STERNEN_STAERKE = 0.45

const NIGHT = {
  skyTop: new Color(0x02050c),
  skyHorizon: new Color(0x0a1424),
  water: new Color(0x040910),
  ambient: 0.08,
  sun: 0.12,
}

const DAY = {
  skyTop: new Color(0x2a6fd0),
  skyHorizon: new Color(0xbcd6ea),
  water: new Color(0x2f6188),
  ambient: 1.05,
  sun: 1.6,
}

export function createScene(options: SceneOptions): SceneHandle {
  const { canvas, testMode = false } = options
  const renderer = new WebGLRenderer({
    canvas,
    antialias: !testMode,
    preserveDrawingBuffer: testMode,
    powerPreference: 'high-performance',
  })
  renderer.setPixelRatio(testMode ? 1 : Math.min(2, window.devicePixelRatio))

  const scene = new Scene()
  const camera = new PerspectiveCamera(38, 1, 1, 60000)

  const skyMaterial = new ShaderMaterial({
    uniforms: {
      top: { value: NIGHT.skyTop.clone() },
      horizon: { value: NIGHT.skyHorizon.clone() },
      sterne: { value: null as Texture | null },
      sternenStaerke: { value: 0 },
    },
    vertexShader: SKY_VERTEX,
    fragmentShader: SKY_FRAGMENT,
    side: BackSide,
    depthWrite: false,
    fog: false,
  })
  const sky = new Mesh(new SphereGeometry(28000, 24, 16), skyMaterial)
  scene.add(sky)

  // Die Wasserflaeche bleibt innerhalb der Himmelskugel, sonst schneidet ihr Rand
  // sichtbar durch den Horizont.
  const water = createWater(44000)
  scene.add(water.mesh)

  /**
   * Der Mond. Er steht dort, woher das Wasser seinen Schein bekommt, sonst wäre die
   * Mondbahn auf der See eine Behauptung ohne Ursache. Nachts sichtbar, tagsüber nicht.
   */
  /**
   * Der Mond kommt als Bild aus der Lieferung (`assets/texturen/mond.png`): Becken,
   * Krater, Randverdunklung. Vorher war er ein Verlauf aus dem Canvas, also eine Scheibe
   * ohne Gesicht. Die Scheibe ist kleiner als der alte Hof, weil das Bild bis an den Rand
   * geht; additiv bleibt sie, damit ihre Kante nicht als Scheibe im Himmel steht.
   */
  /**
   * Die Mondphase.
   *
   * Das gelieferte Bild zeigt den vollen Mond. Die Phase entsteht hier: der Schatten
   * läuft entlang des Terminators, und der ist auf der Scheibe eine Ellipse, keine
   * Gerade. Bei `phase` 1 steht die Grenze am linken Rand (alles hell), bei 0,5 in der
   * Mitte (Halbmond), bei 0 am rechten Rand (Neumond, nichts hell). Der unbeleuchtete
   * Teil bleibt nicht ganz schwarz: das aschgraue Mondlicht gibt es wirklich, und ohne
   * es wäre die Scheibe bei schmaler Sichel nur ein Strich.
   *
   * Die Phase ist nicht Zierde: bei Neumond sieht man vom Rumpf nichts und nur noch die
   * Lichter, und genau das ist die Lage, für die diese App übt.
   */
  const mondPhase = { value: 1 }
  const moon = new Sprite(
    new SpriteMaterial({
      /**
       * Warmes Elfenbein, kein Blau.
       *
       * Mondlicht ist Sonnenlicht, und der Mond selbst ist grau. Dem dunkeladaptierten
       * Auge erscheint er weiß bis leicht gelb, und tief über dem Horizont deutlich wärmer:
       * sein Licht läuft dort durch etwa acht Luftmassen, die das Blau herausstreuen, wie
       * bei der untergehenden Sonne, nur schwächer. Hier stand 0xc8d8f5, ein kühles Blau,
       * also die Hollywood-Nacht und nicht die auf dem Wasser.
       */
      color: 0xf0e2c8,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      blending: AdditiveBlending,
      fog: false,
    }),
  )
  moon.material.onBeforeCompile = (shader) => {
    shader.uniforms.uPhase = mondPhase
    shader.fragmentShader = shader.fragmentShader
      .replace('void main() {', 'uniform float uPhase;\nvoid main() {')
      .replace(
        '#include <map_fragment>',
        `#include <map_fragment>
        #ifdef USE_MAP
          // Die Texturkoordinate der Scheibe, umgerechnet auf einen Kreis um den
          // Ursprung. Sie heißt in three je Karte anders; die des Mondes ist vMapUv.
          float mondX = vMapUv.x * 2.0 - 1.0;
          float mondY = vMapUv.y * 2.0 - 1.0;
          float mondRand = sqrt(max(0.0, 1.0 - mondY * mondY));
          float mondGrenze = (1.0 - 2.0 * uPhase) * mondRand;
          float mondHell = smoothstep(mondGrenze - 0.05, mondGrenze + 0.05, mondX);
          diffuseColor.rgb *= mix(0.035, 1.0, mondHell);
        #endif`,
      )
  }
  moon.material.customProgramCacheKey = () => 'mondphase'
  moon.position.copy(MOON_DIRECTION).multiplyScalar(20000)
  moon.scale.setScalar(MOND_GROESSE)
  moon.renderOrder = -1
  scene.add(moon)

  /** Holt Mond und Sterne aus der Lieferung, sobald `preloadTexturen()` durch ist. */
  function bindeHimmel(): void {
    const mond = textur('mond')
    if (mond) {
      moon.material.map = mond
      moon.material.needsUpdate = true
    }
    const sterne = textur('sternenhimmel')
    if (sterne) {
      skyMaterial.uniforms.sterne!.value = sterne
      skyMaterial.uniforms.sternenStaerke!.value = dayMode ? 0 : STERNEN_STAERKE
      skyMaterial.needsUpdate = true
    }
  }

  const fog = new FogExp2(NIGHT.skyHorizon.getHex(), 0.00006)
  scene.fog = fog

  const ambient = new AmbientLight(0xa8c6ff, NIGHT.ambient)
  scene.add(ambient)
  const sun = new DirectionalLight(0xdfe9ff, NIGHT.sun)
  sun.position.set(-8000, 4000, 6000)

  /**
   * Streiflicht aus der Richtung des Mondes. Ohne das ist der Rumpf nachts ein schwarzes
   * Loch, und man sieht nicht, wie das Fahrzeug steht. Es ist schwach genug, dass die
   * Lichter das Hellste im Bild bleiben: darauf beruhen die Pixelprüfungen.
   */
  const rim = new DirectionalLight(0x9fc0ff, 0)
  rim.position.copy(MOON_DIRECTION).multiplyScalar(4000)
  scene.add(rim)
  scene.add(sun)


  const vesselHolder = new Group()
  scene.add(vesselHolder)

  let model: VesselModel | undefined
  let buoyModel: BuoyModel | undefined
  let buoyIntensity = 0
  let lights: RenderLight[] = []
  let shapes: RenderShape[] = []
  let stateId = ''
  let bearingDeg = 20
  let lookDeg = 0
  let firstPerson = false
  let scenarioDistanceNm = 1
  let elevationNorm = 0.35
  let distanceNm = 0.15
  let dayMode = false
  let nightVision = false
  let timeSec = 0
  /** Das Lichtzeichen, solange ein Schallzeichen läuft: Licht, Zeitleiste und Beginn. */
  let lichtzeichen: { licht: RenderLight; schritte: SignalStep[]; beginn: number } | undefined
  // Bewegung: Dünung und Wellen. Im Prüfmodus aus, damit dieselbe Zeit dasselbe Bild
  // ergibt, und abschaltbar für alle, die weniger Bewegung wollen.
  let motion = !testMode
  let frameHandle = 0
  let disposed = false

  function clearSubject(): void {
    firstPerson = false
    if (buoyModel) {
      disposeBuoy(buoyModel)
      buoyModel = undefined
    }
    if (model) {
      disposeShapes(shapes)
      disposeLights(lights)
      disposeVessel(model)
      shapes = []
      lights = []
      model = undefined
    }
    vesselHolder.clear()
  }

  function loadScenario(id: string): void {
    clearSubject()
    const data = scenarioById(id)
    const state = lightState(data.other.state)
    const type = vessel(state.vessel)
    const hull = type.hull as HullKind
    const delivered = loadedHull(hull)
    model = delivered ? vesselFromHull(delivered, hull, type.lengthM) : buildVessel(hull, type.lengthM)
    lights = createLights(model, state.lights, state.lengthClass)
    shapes = createShapes(model, state.dayShapes)
    setShapesVisible(shapes, dayMode)
    applyDaylight(model, dayMode)
    // Norden ist +Z, Osten ist +X. Der Kurs dreht den Rumpf um die Hochachse.
    const place = positionOf(data.own, data.other)
    model.group.position.set(place.x * NM_IN_METERS, 0, place.y * NM_IN_METERS)
    model.group.rotation.y = (data.other.headingDeg * Math.PI) / 180
    vesselHolder.add(model.group)
    // Die Sektoren richten sich danach, wie der andere UNS sieht.
    bearingDeg = bearingFromOther(data.own, data.other)
    scenarioDistanceNm = data.other.distanceNm ?? 1
    firstPerson = true
    lookDeg = data.other.bearingDeg ?? 0
    stateId = `scenario:${id}`
  }

  function loadBuoy(id: string): void {
    clearSubject()
    const data = buoy(id)
    buoyModel = buildBuoy(data)
    vesselHolder.add(buoyModel.group)
    stateId = `buoy:${id}`
  }

  function loescheLichtzeichen(): void {
    if (!lichtzeichen) return
    disposeLights([lichtzeichen.licht])
    lichtzeichen = undefined
  }

  /** Brennt das Lichtzeichen zu dieser Zeit? Während eines Tons ja, in der Pause nein. */
  function lichtzeichenAn(zeitSec: number): boolean {
    if (!lichtzeichen) return false
    let t = zeitSec - lichtzeichen.beginn
    if (t < 0) return false
    for (const schritt of lichtzeichen.schritte) {
      if (t < schritt.duration) return schritt.kind !== 'silence'
      t -= schritt.duration
    }
    return false
  }

  function loadState(id: string): void {
    loescheLichtzeichen()
    if (model) {
      disposeShapes(shapes)
      disposeLights(lights)
      disposeVessel(model)
      vesselHolder.clear()
    }
    if (buoyModel) clearSubject()
    /**
     * See und Binnen kommen aus getrennten Beständen; für die Szene sind sie dasselbe:
     * ein Fahrzeugtyp, eine Liste Lichter, eine Liste Signalkörper.
     *
     * Die Tragweite ist dabei eine Darstellungsgröße und keine Aussage der Ordnung. Die
     * BinSchStrO nennt für ihre Lichtstärken (stark, hell, gewöhnlich, § 3.01 Nummer 1)
     * Werte in Anlage 5, und die gehört nicht zu Kapitel 3. Der Binnenzustand nimmt
     * deshalb die Klasse seines Fahrzeugtyps, damit das Licht in der Szene abnimmt wie
     * eines dieser Größe; behauptet wird damit nichts, die Oberfläche zeigt für
     * Binnenbilder keine Tragweite.
     */
    const binnen = istBinnen(id)
    const state = binnen ? binnenState(id) : lightState(id)
    const type = vessel(state.vessel)
    const klasse = binnen ? type.lengthClass : lightState(id).lengthClass
    const hull = type.hull as HullKind
    const delivered = loadedHull(hull)
    // Das gelieferte Modell zuerst; fehlt es, bleibt das Lichterbild trotzdem vollständig.
    model = delivered ? vesselFromHull(delivered, hull, type.lengthM) : buildVessel(hull, type.lengthM)
    lights = createLights(model, state.lights, klasse)
    shapes = createShapes(model, state.dayShapes)
    setShapesVisible(shapes, dayMode)
    vesselHolder.add(model.group)
    applyDaylight(model, dayMode)
    stateId = id
  }

  function target(): Vector3 {
    if (buoyModel) return new Vector3(0, buoyModel.lightHeightM * 0.7, 0)
    if (!model) return new Vector3(0, 5, 0)
    return new Vector3(0, model.deckY + model.lengthM * 0.06, 0)
  }

  function placeCamera(): void {
    if (firstPerson) {
      // Die Kamera steht auf der eigenen Brücke und dreht nur den Blick.
      const rad = (lookDeg * Math.PI) / 180
      camera.position.set(0, 9, 0)
      camera.lookAt(Math.sin(rad) * 1000, 6, Math.cos(rad) * 1000)
      camera.near = 1
      camera.far = 60000
      camera.updateProjectionMatrix()
      return
    }
    const d = Math.max(30, distanceNm * NM_IN_METERS)
    const look = target()
    // Augenhöhe wächst mit der Entfernung, bleibt aber unter der Masthöhe:
    // nah schaut man leicht von oben, auf 6 sm liegt alles fast in der Kimm.
    // Beim Seezeichen steht die Kamera auf Augenhöhe eines Bootes: nur so hebt sich das
    // Toppzeichen gegen den Himmel ab, und genau daran wird es gelesen.
    const base = buoyModel ? 1.6 : Math.min(Math.max(d * 0.1, 6), (model?.lengthM ?? 100) * 0.9)
    const eye = buoyModel ? base + elevationNorm * 2.4 : base * (0.35 + elevationNorm * 1.65)
    // Peilung 0 ist recht voraus, dann im Uhrzeigersinn über Steuerbord.
    // Steuerbord liegt bei -X (siehe hulls.ts), deshalb das Minus vor dem Sinus.
    const rad = (bearingDeg * Math.PI) / 180
    camera.position.set(-Math.sin(rad) * d, look.y + eye, Math.cos(rad) * d)
    camera.lookAt(look)
    camera.near = Math.max(0.5, d / 4000)
    camera.far = 60000
    camera.updateProjectionMatrix()
  }

  function applyMode(): void {
    const p = dayMode ? DAY : NIGHT
    /**
     * Wie hell die Nacht ist, hängt am Mond, und zwar alles daran.
     *
     * Der Nachthimmel ist nie schwarz: über dem Horizont leuchten Luftlicht, Sternenlicht
     * und der Rest des Tages, mondlos mit etwa 0,0002 bis 0,001 cd/m². Ein Vollmond hebt
     * das um das Zehn- bis Fünfundzwanzigfache, und genau deshalb sieht man in einer
     * Vollmondnacht kaum Sterne.
     *
     * Das ist keine Zierde, sondern die Aufgabe dieser App. Ein Rumpf bekommt bei Neumond
     * nur Sternenlicht und liegt bei etwa 0,00004 cd/m², also weit unter dem Himmel: er
     * ist dann eine Silhouette, und man erkennt das Fahrzeug an seinen Lichtern, nicht an
     * seiner Gestalt. Bei Vollmond steht es andersherum. Vorher hellte der Mond nur
     * Streiflicht und Wasserschein auf, während Himmel und Umgebungslicht fest standen;
     * der Rumpf war dadurch in jeder Phase heller als der Himmel, und die App lehrte das
     * Falsche.
     *
     * Der Bildschirm hat drei Größenordnungen Dynamik, die Nacht hat zehn. Getroffen wird
     * deshalb die Richtung des Kontrasts, nicht die absolute Zahl.
     */
    const himmelFaktor = dayMode ? 1 : 0.2 + 0.8 * mondPhase.value
    skyMaterial.uniforms.top!.value.copy(p.skyTop).multiplyScalar(himmelFaktor)
    skyMaterial.uniforms.horizon!.value.copy(p.skyHorizon).multiplyScalar(himmelFaktor)
    /**
     * Nachts kommt der Schein auf dem Wasser vom Mond, also nimmt er mit der Phase ab.
     * Ganz weg ist er nie: bei Neumond bleibt das Sternenlicht, und ein völlig schwarzes
     * Wasser wäre kein Bild mehr, sondern eine Fläche.
     */
    const nacht = dayMode
      ? WATER_DAY
      : { ...WATER_NIGHT, sheen: WATER_NIGHT.sheen.clone().multiplyScalar(0.22 + 0.78 * mondPhase.value) }
    water.setPalette(nacht, p.skyHorizon.clone().multiplyScalar(himmelFaktor), 0.00006, dayMode ? 1 : 0)
    /**
     * Das Umgebungslicht ist das, was der Himmel auf den Rumpf wirft, also folgt es ihm.
     * Bei Neumond bleibt ein halbes Prozent: eine Silhouette hat Kanten, und ohne jedes Licht
     * wäre das Fahrzeug ein Loch im Bild statt eines Schattenrisses.
     */
    ambient.intensity = dayMode ? p.ambient : p.ambient * (0.005 + 0.995 * mondPhase.value)
    /**
     * Nachts ist diese Quelle kein Sonnenlicht, sondern das Restlicht aus der anderen
     * Himmelshälfte, und auch das kommt vom Mond. Vorher stand sie fest auf 0,12 und war
     * damit die stärkste Lichtquelle der Nacht, stärker als Umgebungs- und Streiflicht
     * zusammen: sie allein hielt den Rumpf in jeder Phase hell.
     */
    sun.intensity = dayMode ? p.sun : p.sun * (0.005 + 0.995 * mondPhase.value)
    // Das Streiflicht kommt aus der Richtung des Mondes, also fällt es mit ihm aus.
    rim.intensity = dayMode ? 0 : 0.42 * (0.005 + 0.995 * mondPhase.value)
    fog.color.copy(p.skyHorizon).multiplyScalar(himmelFaktor)
    /**
     * Der Vollmond überstrahlt die schwachen Sterne: sichtbar bleiben etwa die bis zur
     * vierten Größenklasse statt bis zur sechsten, also ein Bruchteil. Gedimmt wird das
     * ganze Feld, denn die schwachen fallen dabei zuerst unter die Sichtbarkeit.
     */
    skyMaterial.uniforms.sternenStaerke!.value =
      dayMode || !skyMaterial.uniforms.sterne!.value
        ? 0
        : STERNEN_STAERKE * (1 - 0.7 * mondPhase.value)
    moon.visible = !dayMode
    setShapesVisible(shapes, dayMode)
    if (model) applyDaylight(model, dayMode)
  }

  function resize(): void {
    const w = canvas.clientWidth || 640
    const h = canvas.clientHeight || 400
    renderer.setSize(w, h, false)
    camera.aspect = w / Math.max(1, h)
    camera.updateProjectionMatrix()
  }

  function renderOnce(): void {
    if (disposed) return
    placeCamera()
    updateLights(lights, {
      bearingDeg,
      distanceNm: firstPerson ? scenarioDistanceNm : distanceNm,
      timeSec,
      dayFactor: dayMode ? 1 : 0,
    })
    if (lichtzeichen) {
      updateLights([lichtzeichen.licht], {
        bearingDeg,
        distanceNm: firstPerson ? scenarioDistanceNm : distanceNm,
        timeSec,
        dayFactor: dayMode ? 1 : 0,
      })
      if (!lichtzeichenAn(timeSec)) {
        lichtzeichen.licht.intensity = 0
        lichtzeichen.licht.core.visible = false
        lichtzeichen.licht.halo.visible = false
      }
    }
    applySwell()
    buoyIntensity = buoyModel ? updateBuoyLight(buoyModel, timeSec, dayMode ? 1 : 0) : 0
    water.update(timeSec, waterLights(), MOON_DIRECTION, camera.position)
    renderer.render(scene, camera)
  }

  /**
   * Die Lichter, die sich im Wasser spiegeln sollen: die des Fahrzeugs in Weltkoordinaten
   * und, falls eines dasteht, das Feuer des Seezeichens.
   */
  /**
   * Dünung: das Fahrzeug hebt und rollt leicht. Zwei Sinus mit ungleicher Periode, damit
   * es nicht wie ein Metronom aussieht, und eine Amplitude, die mit der Länge fällt: ein
   * Frachter von 200 Metern liegt ruhiger als ein Sportboot.
   */
  function applySwell(): void {
    if (!model) return
    const group = model.group
    if (!motion) {
      group.position.y = 0
      group.rotation.z = 0
      group.rotation.x = 0
      return
    }
    const laenge = Math.max(8, model.lengthM)
    const ruhe = Math.min(1, 40 / laenge)
    const t = timeSec
    group.position.y = (Math.sin(t * 0.7) * 0.22 + Math.sin(t * 1.13 + 1.7) * 0.12) * ruhe
    group.rotation.z = (Math.sin(t * 0.53 + 0.4) * 0.012 + Math.sin(t * 0.97) * 0.006) * ruhe
    group.rotation.x = Math.sin(t * 0.61 + 2.1) * 0.006 * ruhe
  }

  function waterLights(): WaterLight[] {
    const out: WaterLight[] = []
    for (const light of lights) {
      if (light.intensity <= 0.02) continue
      const world = light.position.clone()
      model?.group.localToWorld(world)
      out.push({ position: world, colour: light.core.material.color.clone(), intensity: light.intensity })
    }
    const feuer = buoyModel?.light
    if (feuer && buoyIntensity > 0.02) {
      const world = new Vector3()
      feuer.core.getWorldPosition(world)
      out.push({
        position: world,
        colour: (feuer.core.material as SpriteMaterial).color.clone(),
        intensity: buoyIntensity,
      })
    }
    return out
  }

  function loop(nowMs: number): void {
    if (disposed) return
    if (!testMode) timeSec = nowMs / 1000
    renderOnce()
    frameHandle = requestAnimationFrame(loop)
  }

  applyMode()
  resize()

  return {
    setStateId(id) {
      if (id !== stateId) loadState(id)
    },
    setBuoyId(id) {
      if (`buoy:${id}` !== stateId) loadBuoy(id)
    },
    setScenarioId(id) {
      if (`scenario:${id}` !== stateId) loadScenario(id)
    },
    setLookDeg(deg) {
      lookDeg = deg
    },
    buoyLight() {
      return buoyIntensity
    },
    sampleBuoyLight() {
      if (!buoyModel?.light) return undefined
      const w = renderer.domElement.clientWidth || 1
      const h = renderer.domElement.clientHeight || 1
      const world = buoyModel.light.core.position.clone()
      buoyModel.group.localToWorld(world)
      const projected = world.clone().project(camera)
      const x = ((projected.x + 1) / 2) * w
      const y = ((1 - projected.y) / 2) * h
      return {
        type: 'buoy',
        colour: buoyModel.character?.colours[0] ?? 'W',
        mount: 'top',
        intensity: buoyIntensity,
        x,
        y,
        onScreen: projected.z < 1 && x >= 0 && x <= w && y >= 0 && y <= h,
      }
    },
    setBearing(deg) {
      bearingDeg = ((deg % 360) + 360) % 360
    },
    setElevation(norm) {
      elevationNorm = Math.min(1, Math.max(0, norm))
    },
    setDistanceNm(nm) {
      distanceNm = Math.min(6.5, Math.max(0.02, nm))
    },
    setDayMode(day) {
      dayMode = day
      applyMode()
    },
    setMondphase(phase) {
      mondPhase.value = Math.min(1, Math.max(0, phase))
      applyMode()
      renderOnce()
    },
    setNightVision(on) {
      nightVision = on
      canvas.style.filter = on ? `url(#${NIGHT_VISION_FILTER_ID})` : ''
    },
    setTime(sec) {
      timeSec = sec
    },
    setMotion(on) {
      motion = on
      water.setMotion(on)
      if (!on) applySwell()
    },
    async preload() {
      await Promise.all([preloadHulls(), preloadTexturen()])
      water.bindeKarten()
      bindeHimmel()
      // Nach dem Laden wird das Fahrzeug neu gebaut, damit es das gelieferte Modell nutzt.
      // Zeigt die Szene inzwischen ein Seezeichen, bleibt es stehen: sonst würde der
      // Nachlauf des Ladens das Bild des Nutzers überschreiben.
      if (stateId && !stateId.startsWith('buoy:') && !stateId.startsWith('scenario:')) {
        const current = stateId
        stateId = ''
        loadState(current)
      }
    },
    fitDistanceNm() {
      if (buoyModel) return 14 / NM_IN_METERS
      const l = model?.lengthM ?? 100
      return Math.max(60, l * 1.9) / NM_IN_METERS
    },
    resize,
    start() {
      if (!frameHandle) frameHandle = requestAnimationFrame(loop)
    },
    stop() {
      if (frameHandle) cancelAnimationFrame(frameHandle)
      frameHandle = 0
    },
    renderOnce,
    setLichtzeichen(schritte) {
      loescheLichtzeichen()
      if (!schritte || schritte.length === 0 || !model) return
      // Gelb und rundum, über allem anderen am Mast: "hell und von allen Seiten sichtbar".
      const [licht] = createLights(
        model,
        [{ type: 'allround', colour: 'yellow', mount: 'masthead-top', tier: 3 }],
        'm20to50',
      )
      if (!licht) return
      lichtzeichen = { licht, schritte, beginn: timeSec }
    },
    sampleLichtzeichen() {
      if (!lichtzeichen) return undefined
      const w = renderer.domElement.clientWidth || 1
      const h = renderer.domElement.clientHeight || 1
      const welt = lichtzeichen.licht.position.clone()
      model?.group.localToWorld(welt)
      const p = welt.project(camera)
      return { x: ((p.x + 1) / 2) * w, y: ((1 - p.y) / 2) * h, an: lichtzeichenAn(timeSec) }
    },
    sampleLights() {
      const w = renderer.domElement.clientWidth || 1
      const h = renderer.domElement.clientHeight || 1
      return lights.map((light) => {
        const world = light.position.clone()
        model?.group.localToWorld(world)
        const projected = world.clone().project(camera)
        const x = ((projected.x + 1) / 2) * w
        const y = ((1 - projected.y) / 2) * h
        return {
          type: light.spec.type,
          colour: light.colour,
          mount: light.spec.mount,
          intensity: light.intensity,
          x,
          y,
          onScreen: projected.z < 1 && x >= 0 && x <= w && y >= 0 && y <= h,
        }
      })
    },
    sampleShapes() {
      const w = renderer.domElement.clientWidth || 1
      const h = renderer.domElement.clientHeight || 1
      return shapes.map((shape) => {
        const world = shape.position.clone()
        model?.group.localToWorld(world)
        const projected = world.clone().project(camera)
        return {
          kind: shape.spec.kind,
          mount: shape.spec.mount,
          visible: shape.object.visible,
          x: ((projected.x + 1) / 2) * w,
          y: ((1 - projected.y) / 2) * h,
        }
      })
    },
    readPixel(x, y) {
      renderOnce()
      const gl = renderer.getContext()
      const ratio = renderer.getPixelRatio()
      const px = Math.round(x * ratio)
      const py = Math.round((renderer.domElement.clientHeight - y) * ratio)
      const buf = new Uint8Array(4)
      gl.readPixels(px, py, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, buf)
      const pixel: [number, number, number, number] = [buf[0]!, buf[1]!, buf[2]!, buf[3]!]
      // Der Rotmodus liegt als Filter über der Fläche, nicht im Framebuffer.
      // Gemessen wird, was der Nutzer sieht, also mit derselben Matrix.
      return nightVision ? applyNightVision(pixel) : pixel
    },
    currentStateId() {
      return stateId
    },
    sampleBeleuchtung() {
      const h = skyMaterial.uniforms.horizon!.value as Color
      return {
        ambient: ambient.intensity,
        sun: sun.intensity,
        rim: rim.intensity,
        sterne: skyMaterial.uniforms.sternenStaerke!.value as number,
        himmel: { r: h.r, g: h.g, b: h.b },
      }
    },
    zoomeAufMond(faktor) {
      camera.clearViewOffset()
      if (faktor <= 1) {
        camera.updateProjectionMatrix()
        return
      }
      const w = renderer.domElement.clientWidth || 1
      const h = renderer.domElement.clientHeight || 1
      // Wo der Mond im vergrößerten Vollbild läge, und dieser Punkt kommt in die Mitte.
      const ndc = moon.position.clone().project(camera)
      const x = ((ndc.x + 1) / 2) * w * faktor
      const y = ((1 - ndc.y) / 2) * h * faktor
      camera.setViewOffset(w * faktor, h * faktor, x - w / 2, y - h / 2, w, h)
      camera.updateProjectionMatrix()
    },
    sampleMoon() {
      const w = renderer.domElement.clientWidth || 1
      const h = renderer.domElement.clientHeight || 1
      const mitte = moon.position.clone().project(camera)
      // Das Sprite steht immer quer zur Kamera: ein Schritt entlang ihrer Hochachse um
      // den halben Maßstab trifft genau den Rand der Scheibe.
      const oben = moon.position.clone()
        .add(camera.up.clone().applyQuaternion(camera.quaternion).multiplyScalar(MOND_GROESSE / 2))
        .project(camera)
      return {
        x: ((mitte.x + 1) / 2) * w,
        y: ((1 - mitte.y) / 2) * h,
        radius: (Math.abs(oben.y - mitte.y) / 2) * h,
        visible: moon.visible && mitte.z < 1,
      }
    },
    dispose() {
      disposed = true
      if (frameHandle) cancelAnimationFrame(frameHandle)
      clearSubject()
      skyMaterial.dispose()
      water.dispose()
      renderer.dispose()
    },
  }
}

